// Pure helpers for keyword data in the report. Truthful by construction:
// every number/trend is shown only when the payload carries it.
const isNum = (v) => typeof v === "number" && Number.isFinite(v);

const LOC = { de: "de-DE", en: "en-GB" };
export const fmtInt = (n, lang = "de") => new Intl.NumberFormat(LOC[lang] || LOC.de).format(n);

// trend_pct -> "+12,5" / "-8" / "0" (no unit); null when not a number.
export function fmtTrend(pct, lang = "de") {
  if (!isNum(pct)) return null;
  const abs = new Intl.NumberFormat(LOC[lang] || LOC.de, { maximumFractionDigits: 1 }).format(Math.abs(pct));
  if (pct > 0) return `+${abs}`;
  if (pct < 0) return `-${abs}`;
  return abs;
}

const COPY = {
  de: {
    line: (kw, n, t) => `Nach „${kw}“ wird in Deutschland etwa ${n}-mal pro Monat gesucht${t ? `, ${t}% gegenüber dem Vorjahr` : ""}.`,
    source: "Quelle: Google-Keyword-Daten",
  },
  en: {
    line: (kw, n, t) => `„${kw}“ is searched about ${n} times per month in Germany${t ? `, ${t}% vs. the previous year` : ""}.`,
    source: "Source: Google keyword data",
  },
};

// ---- Paid tab: "Ihre wichtigsten Suchbegriffe" ----
const DASH = "–";
export const KEYWORD_FOOTNOTE = "Suchbegriffe automatisch aus Google-Daten ermittelt; ChatGPT-Antworten können bei jeder Anfrage abweichen.";

export function trendView(pct, lang = "de") {
  const t = fmtTrend(pct, lang);
  if (t === null) return null;
  return { arrow: pct > 0 ? "▲" : pct < 0 ? "▼" : "→", text: `${t}%`, dir: pct > 0 ? "up" : pct < 0 ? "down" : "flat" };
}

const mapsText = (r) => {
  if (!isNum(r.maps_total)) return DASH; // not measured
  if (isNum(r.maps_rank)) return `Platz ${r.maps_rank} von ${r.maps_total}`;
  return "nicht gefunden";
};

// Organic (non-local-pack) Google rank per keyword — only ever shown when it
// was actually measured (own domain + a completed organic-rank call); never
// shown for a keyword where no own domain exists to match against.
const organicText = (r) => {
  if (!isNum(r.organic_total)) return null; // not measured -> hidden, not a dash
  if (isNum(r.organic_rank)) return `Organische Platzierung: Platz ${r.organic_rank} von ${r.organic_total}`;
  return "Organische Platzierung: nicht gefunden";
};

// The top-3 businesses from that keyword's OWN Maps search (byproduct of the
// same paid call as maps_rank) — names only, never a score, since these
// businesses were never audited (see server.py's _top3_for for the audited,
// scored comparison used elsewhere in the report).
const top3Text = (r) => {
  if (!Array.isArray(r.top3) || !r.top3.length) return null;
  const names = r.top3
    .filter((t) => t && typeof t.name === "string" && t.name.trim())
    .map((t) => t.name.trim());
  return names.length ? `Top: ${names.join(", ")}` : null;
};

const chatgptText = (r, byKeyword) => {
  const c = (byKeyword && byKeyword[r.keyword]) || r.chatgpt;
  if (!c || !isNum(c.asked) || c.asked <= 0 || !isNum(c.mentioned)) return DASH;
  return `in ${c.mentioned} von ${c.asked} Antworten genannt`;
};

// At most 5 rows; null when there is nothing to show.
export function keywordRows(keywords, chatgptVisibility, lang = "de") {
  if (!Array.isArray(keywords)) return null;
  const by = chatgptVisibility && chatgptVisibility.by_keyword;
  const rows = keywords
    .filter((r) => r && typeof r.keyword === "string" && r.keyword.trim())
    .slice(0, 5)
    .map((r) => ({
      keyword: r.keyword,
      searches: isNum(r.monthly_searches) ? fmtInt(r.monthly_searches, lang) : DASH,
      trend: isNum(r.monthly_searches) ? trendView(r.trend_pct, lang) : null,
      maps: mapsText(r),
      organic: organicText(r),
      top3: top3Text(r),
      chatgpt: chatgptText(r, by),
    }));
  return rows.length ? rows : null;
}

// Free tier: { text, source } or null when there is nothing truthful to show.
export function volumeLine(kv, lang = "de") {
  if (!kv || typeof kv.keyword !== "string" || !kv.keyword.trim() || !isNum(kv.monthly_searches) || kv.monthly_searches < 0) return null;
  const c = COPY[lang] || COPY.de;
  return { text: c.line(kv.keyword.trim(), fmtInt(kv.monthly_searches, lang), fmtTrend(kv.trend_pct, lang)), source: c.source };
}
